import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  Menu,
  X,
  Search,
  List
} from 'lucide-react';
import { Language } from '@/types';
import { docGroups, docsRegistry } from '../data/docs-registry';
import { searchDocs } from '../utils/search-engine';
import { prefetchDocRoute } from '../utils/prefetch-routes';
import NovaPageFeedback from './NovaPageFeedback';
import NovaNextStep from './NovaNextStep';
import SEO from '@/components/global/SEO';

const AutoTOC = ({ lang, isRtl }: { lang: string; isRtl: boolean }) => {
  const location = useLocation();
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const updateTOC = () => {
      const elements = Array.from(document.querySelectorAll('main h2, main h3'));
      const items = elements.map((el) => {
        if (!el.id) {
          el.id = el.textContent?.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9\u0600-\u06FF-]/g, '') || Math.random().toString(36).substr(2, 9);
        }
        return {
          id: el.id,
          text: el.textContent || '',
          level: Number(el.tagName.charAt(1)),
        };
      });
      setHeadings(items);
    };

    const rafId = requestAnimationFrame(() => {
      updateTOC();
    });

    const observer = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateTOC, 100);
    });

    const target = document.querySelector('main');
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [location.pathname]);

  // Intersection Observer to track active section
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-120px 0% -70% 0%' }
    );

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className={`text-[11px] font-mono font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-3 ${isRtl ? 'justify-end flex-row-reverse' : ''}`}>
        <List size={13} className="text-[#7F77DD]" />
        <span>{lang === 'en' ? 'On this page' : 'في هذه الصفحة'}</span>
      </div>
      <nav className={`max-h-[calc(100vh-14rem)] overflow-y-auto docs-scrollbar ${isRtl ? 'pl-2' : 'pr-2'}`}>
        <ul className={`space-y-2 border-l border-white/[0.08] pl-3 ${isRtl ? 'border-l-0 border-r pr-3 pl-0 text-right' : 'text-left'}`}>

          {headings.map((h, idx) => {
            const isActive = activeId === h.id;
            return (
              <li key={idx} style={{ paddingLeft: !isRtl && h.level === 3 ? '0.75rem' : 0, paddingRight: isRtl && h.level === 3 ? '0.75rem' : 0 }}>
                <a
                  href={`#${h.id}`}
                  className={`transition-colors duration-150 block leading-normal ${
                    isActive
                      ? 'text-[#7F77DD] font-medium'
                      : 'text-slate-400 hover:text-slate-200'
                  } ${h.level === 3 ? 'text-[11.5px]' : 'text-[12.5px]'}`}
                >
                  {h.text}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

interface NovaDocsLayoutProps {
  children?: React.ReactNode;
  lang: Language;
  rightSidebar?: React.ReactNode;
}

export const NovaDocsLayout: React.FC<NovaDocsLayoutProps> = ({ children, lang, rightSidebar }) => {
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isRtl = lang === 'ar';

  useEffect(() => {
    const currentDoc = docsRegistry.find(doc => doc.href === location.pathname);
    if (currentDoc) {
      const pageTitle = `${currentDoc.title[lang]} | VEXL Nova Documentation`;
      const pageDesc = currentDoc.description[lang];

      document.title = pageTitle;

      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageDesc);
      } else {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        metaDesc.setAttribute('content', pageDesc);
        document.head.appendChild(metaDesc);
      }

      document.documentElement.lang = lang;
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    }
  }, [location.pathname, lang, isRtl]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredResults = searchDocs(searchQuery, lang);

  return (
    <div className="min-h-screen bg-[#02040a] text-slate-300 font-sans selection:bg-[#7F77DD]/30 selection:text-white relative flex flex-col justify-between">
      <SEO 
        title={lang === 'en' ? 'VEXL Nova Docs | The Intelligence Layer' : 'وثائق VEXL Nova | طبقة الذكاء المكانية'} 
        description={lang === 'en' ? 'Official documentation for VEXL Nova GeoAI Assistant.' : 'الوثائق الرسمية للمساعد الذكي VEXL Nova.'} 
        type="FAQPage"
      />

      <div className="w-full">
        {/* Global Search Header Sub-bar */}
        <div className="fixed top-16 left-0 right-0 z-40 bg-[#02040a]/95 backdrop-blur-md border-b border-white/[0.08]">
          <div className="max-w-[1536px] mx-auto px-6 h-13 py-2 flex items-center justify-center">
            <div className="w-full max-w-xl relative group">
              <Search
                className={`absolute top-1/2 -translate-y-1/2 transition-colors ${isRtl ? 'right-3.5' : 'left-3.5'} ${searchQuery ? 'text-[#7F77DD]' : 'text-slate-500 group-focus-within:text-[#7F77DD]'}`}
                size={15}
              />
              <input
                ref={searchInputRef}
                type="text"
                dir="auto"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={() => setIsSearchOpen(true)}
                placeholder={lang === 'en' ? 'Search documentation (e.g. ArcPy, memory, modes)...' : 'ابحث في التوثيق (مثل: الذاكرة، بايثون، الأنماط)...'}
                className={`w-full bg-white/[0.04] border border-white/[0.08] rounded-lg py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#7F77DD]/50 focus:bg-white/[0.06] transition-all
                  ${isRtl ? 'pr-10 pl-20 text-right' : 'pl-10 pr-20 text-left'}`}
              />
              <div className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 flex items-center gap-1`}>
                <kbd className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-[9px] text-slate-400 font-mono">⌘K</kbd>
              </div>

              {/* Search Results Dropdown */}
              <AnimatePresence>
                {isSearchOpen && filteredResults.length > 0 && (
                  <>
                    <div className="fixed inset-0 z-[-1]" onClick={() => setIsSearchOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      className="absolute top-full left-0 right-0 mt-1.5 bg-[#090d16] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 p-1.5"
                    >
                      <div className="px-3 py-1.5 text-[9px] font-mono font-bold text-slate-500 uppercase tracking-wider border-b border-white/5 mb-1">
                        {lang === 'en' ? 'Documentation Pages' : 'صفحات التوثيق'}
                      </div>
                      {filteredResults.map((result) => (
                        <Link
                          key={result.id}
                          to={result.href}
                          onMouseEnter={() => prefetchDocRoute(result.href)}
                          onFocus={() => prefetchDocRoute(result.href)}
                          onClick={() => {
                            setSearchQuery('');
                            setIsSearchOpen(false);
                          }}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors group"
                        >
                          <span className="text-slate-500 group-hover:text-[#7F77DD] transition-colors">{result.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-200 group-hover:text-white truncate">{result.title[lang]}</div>
                            <div className="text-[11px] text-slate-500 truncate">{result.description[lang]}</div>
                          </div>
                          <ChevronRight size={13} className="text-slate-600 group-hover:text-[#7F77DD] transition-colors flex-shrink-0" />
                        </Link>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Content Layout — Full Screen Height Structure */}
        <div className="pt-28 pb-32 w-full mx-auto px-6 lg:px-12">
          <div className={`flex flex-col lg:flex-row justify-between w-full gap-4 lg:gap-8 ${isRtl ? 'lg:flex-row-reverse' : ''}`}>

            {/* Left Sidebar Navigation — Seamless full height with border divider */}
            <aside className={`hidden lg:block lg:w-64 xl:w-72 flex-shrink-0 sticky top-28 z-20 self-start h-[calc(100vh-7rem)] overflow-y-auto docs-scrollbar py-6 ${
              isRtl ? 'pl-6 border-l border-white/[0.06] text-right' : 'pr-6 border-r border-white/[0.06] text-left'
            }`}>
              <nav className="space-y-6">
                {docGroups.map((group) => {
                  const groupDocs = docsRegistry.filter((doc) => doc.groupId === group.id);
                  if (groupDocs.length === 0) return null;

                  return (
                    <div key={group.id} className="space-y-1">
                      <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500 px-3 py-1">
                        {group.label[lang]}
                      </div>
                      <div className="space-y-0.5">
                        {groupDocs.map((item) => {
                          const isActive = location.pathname === item.href;
                          return (
                            <Link
                              key={item.id}
                              to={item.href}
                              onMouseEnter={() => prefetchDocRoute(item.href)}
                              onFocus={() => prefetchDocRoute(item.href)}
                              className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-all text-xs ${
                                isActive
                                  ? 'text-white font-medium bg-white/[0.06] border border-white/[0.08] shadow-sm'
                                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.02]'
                              }`}
                            >
                              <span className={`transition-colors flex-shrink-0 ${isActive ? 'text-[#7F77DD]' : 'text-slate-500'}`}>
                                {item.icon}
                              </span>
                              <span className="truncate">{item.title[lang]}</span>
                              {isActive && (
                                <span className={`w-1.5 h-1.5 rounded-full bg-[#7F77DD] flex-shrink-0 ${isRtl ? 'mr-auto' : 'ml-auto'}`} />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </nav>
            </aside>

            {/* Main Editorial Column */}
            <main className="flex-1 min-w-0 max-w-4xl py-6 px-4 lg:px-8 xl:px-12">
              <div>
                {children || <Outlet />}
              </div>

              {/* Next Step / Learning Path */}
              <NovaNextStep lang={lang} currentPageHref={location.pathname} />

              {/* Page Feedback Section */}
              <NovaPageFeedback lang={lang} pageId={location.pathname} />
            </main>

            {/* Right On-This-Page Table of Contents */}
            <aside className={`hidden xl:block w-60 xl:w-68 flex-shrink-0 sticky top-28 z-20 self-start h-[calc(100vh-7rem)] overflow-y-auto docs-scrollbar py-6 ${
              isRtl ? 'pr-6 border-r border-white/[0.06] text-right' : 'pl-6 border-l border-white/[0.06] text-left'
            }`}>
              {rightSidebar ? rightSidebar : <AutoTOC lang={lang} isRtl={isRtl} />}
            </aside>


          </div>
        </div>

      </div>

      {/* Mobile Menu Trigger Button */}
      <div className="lg:hidden fixed bottom-6 right-6 z-[60]">
        <button 
          onClick={() => setIsMobileNavOpen(true)} 
          className="h-12 px-4 rounded-full bg-[#090d16] border border-white/10 text-slate-200 flex items-center gap-2 shadow-2xl active:scale-95 transition-all text-xs font-semibold"
        >
          <Menu size={16} />
          <span>{lang === 'en' ? 'Menu' : 'القائمة'}</span>
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isMobileNavOpen && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex justify-end" 
            onClick={() => setIsMobileNavOpen(false)}
          >
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }} 
              transition={{ type: 'spring', damping: 25, stiffness: 200 }} 
              className={`w-[85%] max-w-sm h-full bg-[#090d16] border-l border-white/10 p-6 pt-20 overflow-y-auto ${isRtl ? 'text-right' : 'text-left'}`} 
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setIsMobileNavOpen(false)} 
                className={`absolute top-6 ${isRtl ? 'left-6' : 'right-6'} p-2 text-slate-400 hover:text-white`}
              >
                <X size={20} />
              </button>
              <div className="mb-8">
                <nav className="space-y-6">
                  {docGroups.map((group) => {
                    const groupDocs = docsRegistry.filter((doc) => doc.groupId === group.id);
                    if (groupDocs.length === 0) return null;

                    return (
                      <div key={group.id} className="space-y-1.5">
                        <div className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider px-2">
                          {group.label[lang]}
                        </div>
                        <div className="space-y-1">
                          {groupDocs.map((link) => {
                            const isActive = location.pathname === link.href;
                            return (
                              <Link 
                                key={link.id} 
                                to={link.href} 
                                onMouseEnter={() => prefetchDocRoute(link.href)}
                                onFocus={() => prefetchDocRoute(link.href)}
                                onClick={() => setIsMobileNavOpen(false)} 
                                className={`flex items-center gap-3 py-2 px-3 rounded-lg text-xs transition-colors ${
                                  isActive 
                                    ? 'text-white font-medium bg-white/10 border border-white/10' 
                                    : 'text-slate-400 hover:text-white'
                                }`}
                              >
                                <span className={isActive ? 'text-[#7F77DD]' : 'text-slate-500'}>{link.icon}</span>
                                <span>{link.title[lang]}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </nav>
              </div>

              <div className="border-t border-white/5 pt-6">
                <AutoTOC lang={lang} isRtl={isRtl} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NovaDocsLayout;

